import React, { useRef, useState } from "react";
import { Upload, Loader2, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${url}`;
  return url;
}

export function ImageUpload({ value, onChange, label = "Photo", rounded = false, testId = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Please choose a PNG, JPG, WEBP or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const preview = resolveUrl(value);
  const shape = rounded ? "rounded-full" : "rounded-lg";

  return (
    <div>
      {label && <label className="mb-1.5 block text-sm font-medium text-[#0B192C]">{label}</label>}
      <div className="flex items-center gap-4">
        <div className={`relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden border border-slate-200 bg-[#F5F3EF] ${shape}`}>
          {preview ? (
            <img src={preview} alt="preview" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
          ) : (
            <ImageIcon className="h-7 w-7 text-slate-300" />
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70">
              <Loader2 className="h-5 w-5 animate-spin text-[#2563EB]" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
          <button type="button" onClick={pick} disabled={uploading} className="btn-outline-navy px-4 py-2 text-xs" data-testid={`${testId}-btn`}>
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : preview ? "Change" : "Upload"}
          </button>
          {preview && !uploading && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-red-500" data-testid={`${testId}-remove`}>
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      </div>
      <p className="mt-2 text-xs text-slate-400">PNG, JPG, WEBP or GIF · up to 5 MB</p>
    </div>
  );
}

export default ImageUpload;
